import { useState, useEffect } from 'react';
import { Save, FileText } from 'lucide-react';
import { contentAPI } from '@/lib/api';

const AdminContent = () => {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await contentAPI.getAll();
      setContent(response.data);
      
      // If no content exists, seed initial content
      if (response.data.length === 0) {
        await seedInitialContent();
      }
    } catch (error) {
      console.error('Failed to fetch content:', error);
    } finally {
      setLoading(false);
    }
  };

  const seedInitialContent = async () => {
    const initialContent = [
      {
        id: 'home_hero_title',
        page_name: 'home',
        section_key: 'hero_title',
        content_fa: 'مسیر آینده شما از اینجا آغاز می‌شود',
        updated_at: new Date().toISOString(),
      },
      {
        id: 'home_hero_subtitle',
        page_name: 'home',
        section_key: 'hero_subtitle',
        content_fa: 'اطلاعات کامل کشورهای جهان، دانشگاه‌ها، فرصت‌های تحصیلی و موقعیت‌های شغلی را در یک پلتفرم پیدا کنید',
        updated_at: new Date().toISOString(),
      },
      {
        id: 'home_countries_title',
        page_name: 'home',
        section_key: 'countries_title',
        content_fa: 'کشورهای برتر جهان',
        updated_at: new Date().toISOString(),
      },
      {
        id: 'home_countries_subtitle',
        page_name: 'home',
        section_key: 'countries_subtitle',
        content_fa: 'بهترین مقاصد برای مهاجرت، تحصیل و کار',
        updated_at: new Date().toISOString(),
      },
    ];

    for (const item of initialContent) {
      try {
        await contentAPI.create(item);
      } catch (error) {
        console.error('Failed to seed content:', error);
      }
    }
    
    fetchContent();
  };

  const handleUpdate = async (item) => {
    setSaving(true);
    try {
      const updatedItem = {
        ...item,
        updated_at: new Date().toISOString(),
      };
      await contentAPI.update(item.id, updatedItem);
      alert('محتوا با موفقیت به‌روزرسانی شد');
      fetchContent();
    } catch (error) {
      console.error('Failed to update content:', error);
      alert('خطا در به‌روزرسانی محتوا');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (id, newValue) => {
    setContent(content.map(item => 
      item.id === id ? { ...item, content_fa: newValue } : item
    ));
  };

  const groupedContent = content.reduce((acc, item) => {
    if (!acc[item.page_name]) {
      acc[item.page_name] = [];
    }
    acc[item.page_name].push(item);
    return acc;
  }, {});

  if (loading) {
    return <div className="flex justify-center items-center min-h-screen"><div className="text-[#D4AF37]">در حال بارگذاری...</div></div>;
  }

  return (
    <div className="min-h-screen bg-black p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">مدیریت محتوای سایت</h1>
          <p className="text-zinc-400">ویرایش متن‌ها و محتوای صفحات مختلف سایت</p>
        </div>

        {/* Content by Page */}
        {Object.keys(groupedContent).map((pageName) => (
          <div key={pageName} className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="text-[#D4AF37]" size={24} />
              <h2 className="text-2xl font-semibold text-white">
                {pageName === 'home' ? 'صفحه اصلی' : pageName}
              </h2>
            </div>

            <div className="space-y-4">
              {groupedContent[pageName].map((item) => (
                <div key={item.id} className="glass-effect border border-white/10 rounded-xl p-6">
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    {item.section_key.replace(/_/g, ' ')}
                  </label>
                  <textarea
                    value={item.content_fa}
                    onChange={(e) => handleChange(item.id, e.target.value)}
                    className="w-full px-4 py-3 bg-black border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#D4AF37] min-h-[100px] mb-3"
                    rows={3}
                  />
                  <button
                    onClick={() => handleUpdate(item)}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#F3E5AB] transition-colors disabled:opacity-50"
                  >
                    <Save size={16} />
                    {saving ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}

        {content.length === 0 && (
          <div className="text-center text-zinc-400 py-12">
            محتوایی یافت نشد. در حال ایجاد محتوای اولیه...
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminContent;
